import { useMemo, useEffect, useRef } from "react";

import type { CatalogItem, CatalogItemRule } from "../types/catalog";
import { evaluateRules } from "../config/rules";
import type { CatalogContext, ConfigState } from "../config/types";

export type CatalogItemStatus =
  | "available"
  | "selected"
  | "unavailable"
  | "required";

export interface CatalogItemStatusInfo {
  id: string;
  code: string;
  status: CatalogItemStatus;
  available: boolean;
  /**
   * Whether the item is allowed to toggle right now. Mirrors the engine's
   * canToggle: selected items are always deselectable (#696); unselected
   * items can be selected only when available (not gated by an
   * excludes/enables rule).
   */
  canToggle: boolean;
  reason?: string;
  relatedItems?: {
    /**
     * Which kind of rule produced this hint, in the admin's verb so the
     * customer-facing copy stays recognisable to the tenant admin who
     * configured the rule (B10):
     *
     * - `requires`  — an explicit `Requires(Y→X)` rule fired with X
     *   unselected. Renders as "Requires: X" on Y's row. (In practice the
     *   widget's requires-cascade auto-selects X first, so this is
     *   primarily reserved for completeness.)
     * - `enabledBy` — an `Enables(X→Y)` rule is gating Y, no active
     *   enabler. Renders as "Enabled by: X" on Y's row. Replaces the
     *   misleading "Requires:" prefix used pre-B10.
     * - `conflicts` — an `Excludes` rule pair is fired with both sides
     *   selected, or a unilateral excludes is disabling this side.
     */
    type: "requires" | "enabledBy" | "conflicts";
    items: { id: string; code: string; name: string }[];
    hasMore: boolean;
  };
}

export interface UseCatalogItemCompatibilityProps {
  items: CatalogItem[];
  rules: CatalogItemRule[];
  selectedIds: string[];
  /**
   * Default item IDs from the catalog, forwarded into the engine's
   * CatalogContext. No longer drives a deselect lock (the default-anchored
   * lock was removed for #696); kept for context completeness. Pass an empty
   * array when no defaults exist.
   */
  defaultItemIds?: ReadonlyArray<string>;
  onAutoDeselect?: (removedIds: string[]) => void;
}

export interface CatalogItemCompatibilityResult {
  statusMap: Map<string, CatalogItemStatusInfo>;
  getStatus: (id: string) => CatalogItemStatus;
  canToggle: (id: string) => boolean;
  getDisabledReason: (id: string) => string | undefined;
  getRelatedItems: (id: string) => CatalogItemStatusInfo["relatedItems"];
  isAvailable: (id: string) => boolean;
}

export function useCatalogItemCompatibility({
  items,
  rules,
  selectedIds,
  defaultItemIds = [],
  onAutoDeselect,
}: UseCatalogItemCompatibilityProps): CatalogItemCompatibilityResult {
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const itemById = useMemo(
    () => new Map(items.map((i) => [i.id, i])),
    [items],
  );

  const statusMap = useMemo(() => {
    // Build a minimal CatalogContext and ConfigState for the engine.
    // The engine requires a rootItem but this hook operates on child items only.
    const ctx: CatalogContext = {
      rootItem: {
        id: "__compat_root__",
        code: "__compat_root__",
        catalogItemTypeId: "__type__",
        displayOrder: 0,
      } as never,
      items,
      rules,
      defaultItemIds,
    };
    const state: ConfigState = {
      selectedItems: selectedSet,
      pricingOverrides: new Map(),
      originalSnapshot: null,
    };

    const compat = evaluateRules(ctx, state);

    // Build a per-target lookup for enables rules — needed to derive the "Requires: X"
    // dialog text for enables-gated items (the engine exposes isAvailable/getDisabledReason
    // but not the specific enabler item references, which are needed for the UI tooltip).
    const enabledByRulesByTarget = new Map<string, CatalogItemRule[]>();
    for (const rule of rules) {
      if (rule.ruleType !== "enables") continue;
      const list = enabledByRulesByTarget.get(rule.targetItemId) ?? [];
      list.push(rule);
      enabledByRulesByTarget.set(rule.targetItemId, list);
    }

    // Build a per-source lookup for requires rules — needed to derive "required" status
    // for the UI badge (the engine tracks autoSelected but not which source item required
    // each target, which is needed to distinguish "required" from "available" in the status).
    const requiredBySource = new Map<string, string[]>();
    for (const rule of rules) {
      if (rule.ruleType !== "requires") continue;
      if (!selectedSet.has(rule.sourceItemId)) continue;
      const list = requiredBySource.get(rule.targetItemId) ?? [];
      list.push(rule.sourceItemId);
      requiredBySource.set(rule.targetItemId, list);
    }

    const map = new Map<string, CatalogItemStatusInfo>();

    for (const item of items) {
      const id = item.id;
      const isSelected = selectedSet.has(id);

      // An item is a conflict target when it is the itemBId (excludes-rule target) in a
      // conflict pair — both sides are selected. The engine raises compat.conflicts[] for
      // this case rather than returning isAvailable(id)=false, because either side could
      // theoretically be removed. We follow the rule direction (source excludes target) and
      // flag only itemBId as unavailable so the auto-deselect effect removes the intended item.
      const isConflictTarget = isSelected && compat.conflicts.some(
        (c) => c.itemBId === id,
      );
      const available = compat.isAvailable(id) && !isConflictTarget;

      // Baseline reason from the engine for unavailable rows where neither
      // the conflict pair nor the enables-gate branches below fire.
      // Conflict/enables branches override with richer text.
      let reason: string | undefined = !available
        ? (compat.getDisabledReason(id) ?? undefined)
        : undefined;
      let relatedItems: CatalogItemStatusInfo["relatedItems"];

      if (!available) {
        // Determine whether this is a conflict (both sides selected), an unilateral
        // excludes disable (source selected, target not), or an enables-gate disable.
        const conflictingPairs = compat.conflicts.filter(
          (c) => c.itemAId === id || c.itemBId === id,
        );

        if (conflictingPairs.length > 0 || !compat.isAvailable(id)) {
          // Identify which items are conflicting with this one. For conflicts[] entries
          // the other side is whichever of itemAId/itemBId is not `id`. For unilateral
          // excludes (isAvailable=false, no conflict entry), the engine's
          // getRelatedItems() returns the related item IDs.
          let conflictItemIds: string[];
          if (conflictingPairs.length > 0) {
            conflictItemIds = conflictingPairs.map((c) =>
              c.itemAId === id ? c.itemBId : c.itemAId,
            );
          } else {
            // Unilateral exclude: source selected, target disabled (not both selected).
            conflictItemIds = compat.getRelatedItems(id).filter(
              (relId) => selectedSet.has(relId),
            ) as string[];
          }

          const enableRules = enabledByRulesByTarget.get(id);

          if (conflictItemIds.length > 0) {
            const uniqueConflictIds = Array.from(new Set(conflictItemIds));
            const conflictItems = uniqueConflictIds
              .map((srcId) => itemById.get(srcId))
              .filter(Boolean)
              .slice(0, 3);
            reason = `Conflicts with: ${conflictItems.map((i) => i!.name ?? i!.code).join(", ")}`;
            relatedItems = {
              type: "conflicts",
              items: conflictItems.map((i) => ({
                id: i!.id,
                code: i!.code,
                name: i!.name ?? i!.code,
              })),
              hasMore: uniqueConflictIds.length > 3,
            };
          } else if (enableRules && enableRules.length > 0) {
            // Gated by enables rule — no active enabler. Use the admin's
            // verb ("Enabled by") rather than the inverse "Requires" so a
            // tenant admin doing catalog QA recognises their own rule (B10).
            const enablerItems = enableRules
              .map((r) => itemById.get(r.sourceItemId))
              .filter(Boolean)
              .slice(0, 3);
            reason = `Enabled by: ${enablerItems.map((i) => i!.name ?? i!.code).join(", ")}`;
            relatedItems = {
              type: "enabledBy",
              items: enablerItems.map((i) => ({
                id: i!.id,
                code: i!.code,
                name: i!.name ?? i!.code,
              })),
              hasMore: enableRules.length > 3,
            };
          }
        }
      }

      // Determine status.
      let status: CatalogItemStatus;
      if (isSelected) {
        status = "selected";
      } else if (!available) {
        status = "unavailable";
      } else if (requiredBySource.has(id)) {
        status = "required";
      } else {
        status = "available";
      }

      map.set(id, {
        id,
        code: item.code,
        status,
        available,
        canToggle: compat.canToggle(id),
        reason,
        relatedItems,
      });
    }

    return map;
  }, [items, rules, selectedIds, defaultItemIds, selectedSet, itemById]);

  // Auto-deselect items that became unavailable
  const onAutoDeselectRef = useRef(onAutoDeselect);
  onAutoDeselectRef.current = onAutoDeselect;

  useEffect(() => {
    // Only auto-deselect items that became unavailable AND are removable —
    // i.e. stale selections an enables-gate fired against. The canToggle
    // guard is retained for safety so the cleanup never sweeps an item the
    // engine forbids toggling; selected items are deselectable now (#696),
    // so in practice this catches enables-gate-fired stale selections.
    const toRemove = selectedIds.filter((id) => {
      const info = statusMap.get(id);
      return info && !info.available && info.canToggle;
    });
    if (toRemove.length > 0) {
      onAutoDeselectRef.current?.(toRemove);
    }
  }, [statusMap, selectedIds]);

  return {
    statusMap,
    getStatus: (id) => statusMap.get(id)?.status ?? "available",
    canToggle: (id) => {
      // Defer to the engine's canToggle captured at memo time. Mirrors the
      // engine: selected items can always be deselected (#696); unselected
      // items can be selected only when available.
      const info = statusMap.get(id);
      return info?.canToggle ?? true;
    },
    getDisabledReason: (id) => statusMap.get(id)?.reason,
    getRelatedItems: (id) => statusMap.get(id)?.relatedItems,
    isAvailable: (id) => statusMap.get(id)?.available ?? true,
  };
}
